import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

// SVG Icons
const BotIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8" y2="16" /><line x1="16" y1="16" x2="16" y2="16" /></svg>);
const PauseIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>);
const PlayIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>);
const StopIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /></svg>);
const RefreshIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>);
const CheckIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>);
const AlertIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>);
const ClockIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
const MoonIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>);

const GET_HOURLY_SWITCH_STATUS = gql`query GetHourlySwitchStatus { getHourlySwitchStatus { currentHour isWithinWorkHours currentProjection { hour taxedTarget exemptTarget currentTaxed currentExempt taxedPercentage exemptPercentage shouldEnableTaxed shouldEnableExempt } dailyTaxedSales dailyExemptSales dailyTaxedTarget dailyExemptTarget dailyTaxedPercentage dailyExemptPercentage shouldEnableTaxed shouldEnableExempt reason } }`;
const GET_DAILY_TAX_PROJECTIONS = gql`query GetDailyTaxProjections($date: DateTime) { getDailyTaxProjections(date: $date) { hour taxedTarget exemptTarget currentTaxed currentExempt taxedPercentage exemptPercentage shouldEnableTaxed shouldEnableExempt } }`;
const GET_HOURLY_TAX_SWITCH_CONFIG = gql`query GetHourlyTaxSwitchConfig { getHourlyTaxSwitchConfig { id isEnabled intervalSeconds lastExecution lastDecision } }`;
const TOGGLE_HOURLY_TAX_SWITCH = gql`mutation ToggleHourlyTaxSwitch($enabled: Boolean!) { toggleHourlyTaxSwitch(enabled: $enabled) { id isEnabled intervalSeconds lastExecution } }`;
const UPDATE_HOURLY_TAX_SWITCH_INTERVAL = gql`mutation UpdateHourlyTaxSwitchInterval($seconds: Int!) { updateHourlyTaxSwitchInterval(seconds: $seconds) { id isEnabled intervalSeconds } }`;

export const HourlyTaxSwitchPanel: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [configInterval, setConfigInterval] = useState(60);

  const { data: statusData, loading: statusLoading, refetch: refetchStatus } = useQuery(GET_HOURLY_SWITCH_STATUS, { pollInterval: autoRefresh ? refreshInterval * 1000 : 0 });
  const { data: projectionsData, loading: projectionsLoading, refetch: refetchProjections } = useQuery(GET_DAILY_TAX_PROJECTIONS, { pollInterval: autoRefresh ? refreshInterval * 1000 : 0 });
  const { data: configData, loading: configLoading, refetch: refetchConfig } = useQuery(GET_HOURLY_TAX_SWITCH_CONFIG, { pollInterval: autoRefresh ? refreshInterval * 1000 : 0 });

  const [toggleSwitch, { loading: toggleLoading }] = useMutation(TOGGLE_HOURLY_TAX_SWITCH, { onCompleted: () => { refetchConfig(); setTimeout(() => refetchStatus(), 1000); } });
  const [updateInterval, { loading: intervalLoading }] = useMutation(UPDATE_HOURLY_TAX_SWITCH_INTERVAL, { onCompleted: () => refetchConfig() });

  const status = statusData?.getHourlySwitchStatus;
  const projections = projectionsData?.getDailyTaxProjections || [];
  const config = configData?.getHourlyTaxSwitchConfig;

  const formatCurrency = (value: number) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value);
  const formatHour = (hour: number) => `${hour.toString().padStart(2, '0')}:00`;
  const formatTime = (dateString: string | null) => !dateString ? 'Nunca' : new Date(dateString).toLocaleString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const handleToggleSwitch = async () => { try { await toggleSwitch({ variables: { enabled: !config?.isEnabled } }); } catch { alert('Error'); } };
  const handleUpdateInterval = async () => { if (configInterval < 10 || configInterval > 3600) { alert('Intervalo: 10-3600s'); return; } try { await updateInterval({ variables: { seconds: configInterval } }); } catch { alert('Error'); } };
  const handleManualRefresh = () => { refetchStatus(); refetchProjections(); refetchConfig(); };

  if (statusLoading || projectionsLoading || configLoading) {
    return <div className="htsp-panel"><div className="htsp-loading"><div className="htsp-spinner"></div><span>Cargando...</span></div><style>{styles}</style></div>;
  }

  if (!status) {
    return <div className="htsp-panel"><div className="htsp-error"><AlertIcon /> Error al cargar</div><style>{styles}</style></div>;
  }

  const isActive = config?.isEnabled || false;
  const currentInterval = config?.intervalSeconds || 60;

  return (
    <div className="htsp-panel">
      <div className="htsp-header">
        <h2><BotIcon /> Control Automático MOD</h2>
        <p>Gestiona facturas gravadas/exentas según metas horarias</p>
      </div>

      {/* Automation Control */}
      <div className={`htsp-control-card ${isActive ? 'active' : ''}`}>
        <div className="htsp-control-status">
          <div className={`htsp-status-icon ${isActive ? 'active' : ''}`}>
            {isActive ? <BotIcon /> : <PauseIcon />}
          </div>
          <div className="htsp-status-info">
            <h3>{isActive ? 'Modo Automático ACTIVO' : 'Modo Automático DESACTIVADO'}</h3>
            <p>{isActive ? `Ejecutando cada ${currentInterval}s` : 'Control manual activado'}</p>
            {config?.lastExecution && <small>Última: {formatTime(config.lastExecution)}</small>}
          </div>
        </div>
        <div className="htsp-control-actions">
          <button className={`htsp-btn ${isActive ? 'htsp-btn-danger' : 'htsp-btn-primary'}`} onClick={handleToggleSwitch} disabled={toggleLoading}>
            {toggleLoading ? '...' : isActive ? <><StopIcon /> Desactivar</> : <><PlayIcon /> Activar</>}
          </button>
          <div className="htsp-interval-config">
            <label>Intervalo:</label>
            <input type="number" min="10" max="3600" value={configInterval} onChange={(e) => setConfigInterval(parseInt(e.target.value) || 60)} />
            <button className="htsp-btn htsp-btn-secondary htsp-btn-sm" onClick={handleUpdateInterval} disabled={intervalLoading || configInterval === currentInterval}>
              <CheckIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Refresh Control */}
      <div className="htsp-refresh-bar">
        <label className="htsp-checkbox">
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          Auto-actualizar cada
          <input type="number" min="5" max="300" value={refreshInterval} onChange={(e) => setRefreshInterval(parseInt(e.target.value) || 60)} disabled={!autoRefresh} className="htsp-input-sm" />s
        </label>
        <button className="htsp-btn htsp-btn-secondary" onClick={handleManualRefresh}><RefreshIcon /> Actualizar</button>
      </div>

      {/* Outside Work Hours */}
      {!status.isWithinWorkHours && (
        <div className="htsp-notice"><MoonIcon /><div><strong>Fuera de Horario</strong><p>Sistema bloqueado</p></div></div>
      )}

      {/* Switch States */}
      <div className="htsp-states-grid">
        <div className={`htsp-state-card ${status.shouldEnableTaxed ? 'enabled' : 'disabled'}`}>
          <h4>Facturas Gravadas</h4>
          <div className={`htsp-state-badge ${status.shouldEnableTaxed ? 'on' : 'off'}`}>{status.shouldEnableTaxed ? 'SI' : 'NO'}</div>
          <div className="htsp-state-info">
            <div><span>Meta</span><strong>{formatCurrency(status.dailyTaxedTarget)}</strong></div>
            <div><span>Actual</span><strong>{formatCurrency(status.dailyTaxedSales)}</strong></div>
            <div><span>%</span><strong>{status.dailyTaxedPercentage.toFixed(1)}%</strong></div>
          </div>
          <div className="htsp-progress"><div className="htsp-progress-fill" style={{ width: `${Math.min(status.dailyTaxedPercentage, 100)}%` }} /></div>
        </div>
        <div className={`htsp-state-card ${status.shouldEnableExempt ? 'enabled' : 'disabled'}`}>
          <h4>Facturas Exentas</h4>
          <div className={`htsp-state-badge ${status.shouldEnableExempt ? 'on' : 'off'}`}>{status.shouldEnableExempt ? 'SI' : 'NO'}</div>
          <div className="htsp-state-info">
            <div><span>Meta</span><strong>{formatCurrency(status.dailyExemptTarget)}</strong></div>
            <div><span>Actual</span><strong>{formatCurrency(status.dailyExemptSales)}</strong></div>
            <div><span>%</span><strong>{status.dailyExemptPercentage.toFixed(1)}%</strong></div>
          </div>
          <div className="htsp-progress"><div className="htsp-progress-fill" style={{ width: `${Math.min(status.dailyExemptPercentage, 100)}%` }} /></div>
        </div>
      </div>

      {/* Decision Reason */}
      <div className="htsp-reason"><strong>Decisión:</strong> {status.reason}</div>

      {/* Projections Table */}
      <div className="htsp-projections">
        <h3><ClockIcon /> Proyecciones por Hora</h3>
        <div className="htsp-table-wrapper">
          <table className="htsp-table">
            <thead>
              <tr>
                <th>Hora</th>
                <th>Meta Gravada</th>
                <th>Actual</th>
                <th>%</th>
                <th>Estado</th>
                <th>Meta Exenta</th>
                <th>Actual</th>
                <th>%</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {projections.map((p: any) => (
                <tr key={p.hour} className={p.hour === status.currentHour ? 'current' : ''}>
                  <td className="htsp-hour">{formatHour(p.hour)}{p.hour === status.currentHour && <span className="htsp-current-badge">Actual</span>}</td>
                  <td>{formatCurrency(p.taxedTarget)}</td>
                  <td>{formatCurrency(p.currentTaxed)}</td>
                  <td>{p.taxedPercentage.toFixed(1)}%</td>
                  <td><span className={`htsp-badge ${p.shouldEnableTaxed ? 'on' : 'off'}`}>{p.shouldEnableTaxed ? 'ON' : 'OFF'}</span></td>
                  <td>{formatCurrency(p.exemptTarget)}</td>
                  <td>{formatCurrency(p.currentExempt)}</td>
                  <td>{p.exemptPercentage.toFixed(1)}%</td>
                  <td><span className={`htsp-badge ${p.shouldEnableExempt ? 'on' : 'off'}`}>{p.shouldEnableExempt ? 'ON' : 'OFF'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Summary */}
      <div className="htsp-summary">
        <h3>Resumen del Día</h3>
        <div className="htsp-summary-grid">
          <div className="htsp-summary-item"><span>Gravadas</span><strong>{formatCurrency(status.dailyTaxedSales)}</strong></div>
          <div className="htsp-summary-item"><span>Exentas</span><strong>{formatCurrency(status.dailyExemptSales)}</strong></div>
          <div className="htsp-summary-item"><span>Meta Gravada</span><strong>{formatCurrency(status.dailyTaxedTarget)}</strong></div>
          <div className="htsp-summary-item"><span>Meta Exenta</span><strong>{formatCurrency(status.dailyExemptTarget)}</strong></div>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .htsp-panel { max-width: 1100px; margin: 0 auto; }

  .htsp-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
    border-radius: 8px;
    color: #fff;
    margin-bottom: 16px;
  }
  .htsp-header h2 { margin: 0 0 4px 0; font-size: 1rem; display: flex; align-items: center; gap: 8px; }
  .htsp-header p { margin: 0; font-size: 0.75rem; opacity: 0.9; }

  .htsp-control-card {
    background: #fff;
    border: 2px solid #dbeafe;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }
  .htsp-control-card.active { border-color: #22c55e; background: #f0fdf4; }

  .htsp-control-status { display: flex; align-items: center; gap: 12px; }

  .htsp-status-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
  }
  .htsp-status-icon.active { background: #dcfce7; color: #166534; }

  .htsp-status-info h3 { margin: 0 0 4px 0; font-size: 0.9rem; color: #1e3a8a; }
  .htsp-status-info p { margin: 0; font-size: 0.75rem; color: #64748b; }
  .htsp-status-info small { font-size: 0.65rem; color: #94a3b8; }

  .htsp-control-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

  .htsp-interval-config {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #64748b;
  }
  .htsp-interval-config input {
    width: 60px;
    padding: 4px 8px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    text-align: center;
    font-size: 0.8rem;
  }

  .htsp-refresh-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .htsp-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #475569;
    cursor: pointer;
  }
  .htsp-checkbox input[type="checkbox"] { width: 16px; height: 16px; }

  .htsp-input-sm {
    width: 50px;
    padding: 4px 6px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    text-align: center;
    font-size: 0.75rem;
  }

  .htsp-notice {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    margin-bottom: 12px;
    color: #92400e;
  }
  .htsp-notice strong { display: block; font-size: 0.85rem; }
  .htsp-notice p { margin: 0; font-size: 0.75rem; }

  .htsp-states-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }

  .htsp-state-card {
    background: #fff;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    text-align: center;
  }
  .htsp-state-card.enabled { border-color: #22c55e; border-left-width: 4px; }
  .htsp-state-card.disabled { border-color: #ef4444; border-left-width: 4px; }
  .htsp-state-card h4 { margin: 0 0 10px 0; font-size: 0.85rem; color: #1e3a8a; }

  .htsp-state-badge {
    display: inline-block;
    padding: 8px 24px;
    border-radius: 6px;
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 12px;
  }
  .htsp-state-badge.on { background: #dcfce7; color: #166534; }
  .htsp-state-badge.off { background: #fee2e2; color: #b91c1c; }

  .htsp-state-info {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 10px;
  }
  .htsp-state-info > div {
    padding: 6px;
    background: #f8fafc;
    border-radius: 4px;
  }
  .htsp-state-info span { display: block; font-size: 0.6rem; color: #64748b; }
  .htsp-state-info strong { font-size: 0.8rem; color: #1e3a8a; }

  .htsp-progress {
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
  }
  .htsp-progress-fill {
    height: 100%;
    background: #3b82f6;
    transition: width 0.3s;
  }
  .htsp-state-card.enabled .htsp-progress-fill { background: #22c55e; }
  .htsp-state-card.disabled .htsp-progress-fill { background: #ef4444; }

  .htsp-reason {
    padding: 12px 16px;
    background: #eff6ff;
    border-radius: 6px;
    margin-bottom: 12px;
    font-size: 0.8rem;
    color: #1e40af;
    border-left: 4px solid #3b82f6;
  }

  .htsp-projections {
    background: #fff;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .htsp-projections h3 { margin: 0 0 12px 0; font-size: 0.85rem; color: #1e3a8a; display: flex; align-items: center; gap: 6px; }

  .htsp-table-wrapper { overflow-x: auto; }
  .htsp-table { width: 100%; border-collapse: collapse; font-size: 0.7rem; }
  .htsp-table th, .htsp-table td { padding: 8px 6px; text-align: center; border-bottom: 1px solid #e2e8f0; }
  .htsp-table th { background: #f8fafc; font-weight: 600; color: #64748b; font-size: 0.6rem; text-transform: uppercase; }
  .htsp-table tr.current { background: #eff6ff; }

  .htsp-hour { font-weight: 600; color: #1e3a8a; white-space: nowrap; }
  .htsp-current-badge {
    display: inline-block;
    padding: 2px 6px;
    background: #2563eb;
    color: #fff;
    border-radius: 4px;
    font-size: 0.5rem;
    margin-left: 4px;
  }

  .htsp-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.6rem;
    font-weight: 600;
  }
  .htsp-badge.on { background: #dcfce7; color: #166534; }
  .htsp-badge.off { background: #fee2e2; color: #b91c1c; }

  .htsp-summary {
    background: #fff;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    padding: 16px;
  }
  .htsp-summary h3 { margin: 0 0 12px 0; font-size: 0.85rem; color: #1e3a8a; }

  .htsp-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }
  .htsp-summary-item {
    text-align: center;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }
  .htsp-summary-item span { display: block; font-size: 0.65rem; color: #64748b; margin-bottom: 4px; }
  .htsp-summary-item strong { font-size: 0.9rem; color: #1e3a8a; }

  /* Buttons */
  .htsp-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  .htsp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .htsp-btn-primary { background: #2563eb; color: #fff; }
  .htsp-btn-primary:hover:not(:disabled) { background: #1e40af; }
  .htsp-btn-secondary { background: #e2e8f0; color: #475569; }
  .htsp-btn-secondary:hover:not(:disabled) { background: #cbd5e1; }
  .htsp-btn-danger { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
  .htsp-btn-danger:hover:not(:disabled) { background: #fee2e2; }
  .htsp-btn-sm { padding: 6px 8px; }

  .htsp-loading, .htsp-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 48px;
    color: #64748b;
    font-size: 0.85rem;
  }
  .htsp-error { color: #b91c1c; background: #fef2f2; border-radius: 8px; }

  .htsp-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: htsp-spin 0.6s linear infinite;
  }
  @keyframes htsp-spin { to { transform: rotate(360deg); } }

  @media (max-width: 700px) {
    .htsp-states-grid { grid-template-columns: 1fr; }
    .htsp-summary-grid { grid-template-columns: repeat(2, 1fr); }
    .htsp-control-card { flex-direction: column; align-items: flex-start; }
  }
`;
